import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  FormContainer,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useMemo } from "react";
import { Grid } from "@mui/material";
import { listMainGenerics } from "../../../../../../../services/generic.service";
import {
  ticketAutRecRuleTasyOrientationSchema,
  TTicketAutRecRuleTasyOrientationForm,
} from "./ticketAutRecRuleTasyOrientationSchema";
import {
  createTicketAutRecRuleTasyOrientation,
  getTicketAutRecRuleTasyOrientationById,
  updateTicketAutRecRuleTasyOrientationById,
} from "../../../../../../../services/ticketAutRecRuleTasyOrientation.service";
import { ITicketAutRecRuleTasyOrientationProps } from "./models";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.ticketAutRecRuleTasyOrientation.";

const TicketAutRecRuleTasyOrientation = ({
  id,
  ticketAutomationReceivedRuleId,
  onBackButton,
  automationCode,
}: ITicketAutRecRuleTasyOrientationProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutRecRuleTasyOrientation,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutRecRuleTasyOrientationById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutRecRuleTasyOrientationById,
  );

  const typeTasyOrientationParams = useMemo(() => {
    const baseParams: any = { Identifier: "TypeTasyOrientation" };
    const currentAutomationCode = automationCode ?? "";

    // Automações de agendamento que podem visualizar o Code 4 (Orientações da Agenda)
    const scheduleAutomationCodes = ["20", "21", "25", "26"];

    if (scheduleAutomationCodes.includes(currentAutomationCode)) {
      // Automações 20, 25 e 26: mantém o Code 3 e inclui o Code 4
      if (["20", "25", "26"].includes(currentAutomationCode)) {
        baseParams.Codes = ["3", "4"];
      } else {
        // Automação 21: mantém todos os códigos existentes e inclui o Code 4
        baseParams.Codes = ["1", "2", "3", "4"];
      }
    } else {
      // Demais automações: mantém o comportamento atual, ocultando o Code 4
      baseParams.Codes = ["1", "2", "3"];
    }

    return baseParams;
  }, [automationCode]);

  const updateHandler = (v: TTicketAutRecRuleTasyOrientationForm, id: string) =>
    update({
      item: {
        id: id,
        ...v,
        typeTasyOrientationId: v.typeTasyOrientation.id,
        sendWhenId: v.sendWhen.id,
        enable: v.enable,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
      },
      id: id,
    });

  const saveHandler = (v: TTicketAutRecRuleTasyOrientationForm) =>
    save({
      ...v,
      typeTasyOrientationId: v.typeTasyOrientation.id,
      sendWhenId: v.sendWhen.id,
      enable: v.enable,
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
    });

  const getHandler = useCallback(
    async (
      id: string,
    ): Promise<Treturn<TTicketAutRecRuleTasyOrientationForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TTicketAutRecRuleTasyOrientationForm = {
          ...data,
          typeTasyOrientation: {
            id: data.typeTasyOrientation.id,
            name: data.typeTasyOrientation.name,
          },
          sendWhen: {
            id: data.sendWhen.id,
            name: data.sendWhen.name,
          },
          enable: data.enable,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TTicketAutRecRuleTasyOrientationForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "ticket_autrecrule_tasy_orientation")}
      schema={ticketAutRecRuleTasyOrientationSchema}
      subtitle={t(translationPath + "new_ticket_autrecrule_tasy_orientation")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item sm={12} textAlign={"end"}>
            <SwitchInput name="enable" label={t(translationPath + "enable")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              getOptionLabel={({ name }) => name}
              size="small"
              label={t(translationPath + "type_tasy_orientation")}
              name="typeTasyOrientation"
              request={listMainGenerics}
              params={typeTasyOrientationParams}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              getOptionLabel={({ name }) => name}
              size="small"
              label={t(translationPath + "send_when")}
              name="sendWhen"
              request={listMainGenerics}
              params={{ Identifier: "SendWhen" }}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default TicketAutRecRuleTasyOrientation;
